<template>
  <div class="row">
    <div class="col-md-3 col-lg-2 mb-10">
      <MemberProfilesNav :tabs="setting_tabs" />
    </div>
    <div class="col-md-8 col-sm-12 col-lg-10 border-left pl-lg-30 setting_wrapper settings_panel">
      <div
        v-if="!feature.available"
        class="alert alert-disabled"
      >
        <div class="alert-inner-wrapper">
          <div class="alert-body d-flex align-items-center">
            <i class="hi hi-16 hi-lock mr-15 text-blue ml-5" />
            <span>{{ feature.availability_message }}</span>
          </div>
          <div class="alert-action">
            <a
              :href="billingLink"
              class="btn btn-sm btn-primary mr-15"
            >
              View plans & add-ons
            </a>
          </div>
        </div>
      </div>

      <div :class="{ 'section-disabled': !feature.available }">
        <div class="mb-20">
          <h5 class="setting-title">Email notifications</h5>
          <p class="text-muted">
            When creating new accounts, this sets whether the members will receive ANY email communication from
            Hubstaff. This includes notifications about their own work as well as anyone they might manage.
          </p>
          <p class="text-muted">
            This setting can be altered when creating the accounts or individually overridden afterwards in the table
            below.
          </p>

          <div class="d-inline-flex flex-column mt-30">
            <label class="control-label setting-sub-title">
              Default
              <i
                v-tooltip="{ placement: 'right' }"
                class="hi-info text-default a-tooltip ml-5"
                title="By turning on this setting, the default for new accounts will be to receive email communications from Hubstaff. This can be changed during account creation or overridden below."
              ></i>
            </label>
            <AppOnOffToggle
              :key="changedKey"
              :value="allowReceivingEmails"
              @input="onShowMemberChangeNotificationDialog"
            >
              <div class="d-flex align-items-center">Allow members to receive Hubstaff emails</div>
            </AppOnOffToggle>
          </div>
        </div>

        <AppTableLoading
          v-if="loading && feature.available"
          :columns="3"
        ></AppTableLoading>

        <MemberNotificationsTable
          v-else-if="feature.available"
          :show-loading="loading"
          :members="members"
          :organization-id="organization.id"
          :search="search"
          @update-member="onChangeNotificationSetting"
          @search-updated="onSearchTermUpdated"
        />
        <AppPagination
          v-if="showPagination"
          :pagination="pagination"
          :resource="{ one: 'member', many: 'members' }"
          @page-changed="onPageChanged"
        />

        <MemberChangeNotificationDialog
          v-if="showMemberChangeNotificationDialog"
          id="confirm-change-notifications"
          key="confirm-change-notifications"
          :needs-confirm-change="match_default_members_count"
          :confirm-override="confirmOverride"
          :setting-value="!organization.allow_receiving_emails"
          @open-confirm-override-dialog="onOpenOverridesDialog"
          @close="onCloseMemberChangeNotificationDialog"
        />

        <MemberUpdateOverridesDialog
          v-if="showMemberUpdateOverridesDialog"
          id="update-overrides"
          key="update-overrides"
          :overrides-count="match_default_members_count"
          @close="onCloseMemberUpdateOverridesDialog"
          @close-confirmed="onConfirmOverride"
        />
      </div>
    </div>
  </div>
</template>

<script>
// Inertia helpers/utils
import { InertiaFlashWatcher, InertiaLoadingWatcher } from '~/utils/inertia';
// Components
import MemberProfilesNav from '~/components/settings/MemberProfilesNav.vue';
import MemberNotificationsTable from '~/components/members/MemberNotificationsTable.vue';
import MemberChangeNotificationDialog from '~/components/members/MemberChangeNotificationDialog.vue';
import MemberUpdateOverridesDialog from '~/components/members/MemberUpdateOverridesDialog.vue';
import AppPagination from '~/components/AppPagination.vue';
import { applyTemplate } from '~/utils/url_template';
import { UPDATE_NOTIFICATION_SETTINGS_URL } from '~/app_constants';
import { OrganizationsResource } from '~/resources';

export default {
  name: 'MemberProfiles',
  components: {
    MemberProfilesNav,
    MemberNotificationsTable,
    MemberChangeNotificationDialog,
    MemberUpdateOverridesDialog,
    AppPagination,
  },
  mixins: [InertiaFlashWatcher, InertiaLoadingWatcher],
  props: {
    feature: {
      type: Object,
      required: true,
    },
    members: {
      type: Array,
      required: true,
    },
    pagination: {
      type: Object,
      required: true,
    },
    search: {
      type: String,
      default: '',
    },
    match_default_members_count: {
      type: Number,
      default: 0,
    },
    organization: {
      type: Object,
      required: true,
    },
    setting_tabs: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      member: null,
      showMemberChangeNotificationDialog: false,
      showMemberUpdateOverridesDialog: false,
      confirmOverride: false,
      changedKey: 0,
    };
  },
  computed: {
    allowReceivingEmails() {
      return this.organization.allow_receiving_emails;
    },
    billingLink() {
      return OrganizationsResource.billingLink({ id: this.organization.id });
    },
    showPagination() {
      return !this.loading && this.pagination?.total_pages > 1;
    },
  },
  methods: {
    onShowMemberChangeNotificationDialog() {
      this.showMemberChangeNotificationDialog = true;
    },
    onCloseMemberChangeNotificationDialog() {
      this.showMemberChangeNotificationDialog = false;
      this.changedKey += 1;
      this.confirmOverride = false;
    },
    onOpenOverridesDialog() {
      this.showMemberUpdateOverridesDialog = true;
    },
    onCloseMemberUpdateOverridesDialog() {
      this.showMemberUpdateOverridesDialog = false;
      this.onCloseMemberChangeNotificationDialog();
    },
    onConfirmOverride(val) {
      this.showMemberUpdateOverridesDialog = false;
      this.confirmOverride = val;
    },
    onChangeNotificationSetting(userId, settingValue) {
      const { origin } = window.location;
      const url = new URL(applyTemplate(UPDATE_NOTIFICATION_SETTINGS_URL, { id: this.organization.id }), origin);
      const organizationsUrlString = url.toString();
      const options = {
        onSuccess: () => {
          this.$appMessage(`Email notifications turned ${settingValue ? 'on' : 'off'}`);
        },
      };

      this.$inertia.post(
        organizationsUrlString,
        { organization: { user_id: userId, allow_receiving_emails: settingValue } },
        options,
      );
    },
    onSearchTermUpdated(search) {
      this.refresh({ search, page: 1 });
    },
    onPageChanged(page) {
      this.refresh({ page });
    },
    refresh(params) {
      const url = new URL(window.location);

      for (const key in params) {
        url.searchParams.set(key, params[key]);
      }

      this.$inertia.visit(url.toString());
    },
  },
};
</script>
